/**
 * Video conferencing platform detection and icon resolution.
 *
 * Contains platform definitions, URL validation, icon ladder logic, and
 * accessible label helpers for meeting join links. Extracted from
 * meetingHelpers.ts so the platform-specific logic lives in its own module.
 *
 * Re-exported for backward compatibility through meetingHelpers.ts.
 */

// ============================================================================
// Video Platform Detection
// ============================================================================

/** Video conferencing platform detection */
export type VideoPlatform =
  | 'google-meet'
  | 'zoom'
  | 'teams'
  | 'luma'
  | 'calendly'
  | 'goldcast'
  | 'generic';

/**
 * Check if a string is a valid URL (http or https).
 */
function isValidUrl(str: string): boolean {
  try {
    const parsed = new URL(str);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

// Heuristic for "is this freeform text likely a URL the user pasted into a
// non-URL field (e.g. ahoy_location)?" A previous broader heuristic
// (`includes('.')`) misclassified plain dotted text like "123 Main St." or
// "Room.42" as a URL, lighting up a phantom Join button.
//
// Recognises two shapes:
//   1. Explicit scheme: starts with `http://` / `https://`.
//   2. Scheme-less hostname: one or more domain labels separated by dots,
//      ending in a 2+ char alphabetic TLD, optionally followed by a port
//      or path. Accepts real conferencing links pasted without a scheme
//      (`meet.google.com/xyz`, `zoom.us/j/123`) while rejecting "Room.42"
//      (numeric TLD), "Building.A" (1-char TLD), and anything containing
//      whitespace ("123 Main St., NYC").
const HOSTNAME_LIKE_RE =
  /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(:\d+)?(\/.*)?$/i;
const HTTP_SCHEME_RE = /^https?:\/\//i;

export function looksLikeUrl(s: string): boolean {
  if (HTTP_SCHEME_RE.test(s)) {
    return true;
  }
  if (/\s/.test(s)) {
    return false;
  }
  return HOSTNAME_LIKE_RE.test(s);
}

interface PlatformDefinition {
  readonly label: string;
  /** Bundled brand mark, or null to fall through to the icon ladder. */
  readonly icon: string | null;
  /**
   * Monochrome marks are tinted to `currentColor` by the renderer so they stay
   * legible in dark mode. Multi-colour brand art renders as-is.
   */
  readonly mono: boolean;
  /** Hostname fragments that identify this platform. */
  readonly hosts: readonly string[];
}

/**
 * Platforms we ship a brand mark for.
 *
 * Membership is driven by production reach, not by which vendors are famous:
 * Meet/Zoom/Teams dominate, and Luma, Goldcast and Calendly are the providers
 * that actually appear across multiple orgs' calendars. Anything else resolves
 * through the icon ladder (see getMeetingIconSources) rather than getting a
 * bundled asset it doesn't earn.
 *
 * Cal.com is deliberately absent despite its reach (16 orgs): the only SVG
 * available for it is the *wordmark*, which reduces to illegible lettering at
 * 16px. Its own favicon is a proper square logomark, so it reads better through
 * the favicon rung — which the chip now keeps legible in dark mode.
 */
const PLATFORM_DEFINITIONS: Record<
  Exclude<VideoPlatform, 'generic'>,
  PlatformDefinition
> = {
  'google-meet': {
    label: 'Google Meet',
    icon: '/assets/figma/google-meet-icon.svg',
    mono: false,
    hosts: ['meet.google.com', 'hangouts.google.com'],
  },
  zoom: {
    label: 'Zoom',
    icon: '/assets/figma/zoom-icon.svg',
    mono: false,
    hosts: ['zoom.us', 'zoomgov.com'],
  },
  teams: {
    label: 'Microsoft Teams',
    icon: '/assets/figma/teams-icon.svg',
    mono: false,
    hosts: ['teams.microsoft.com', 'teams.live.com'],
  },
  luma: {
    label: 'Luma',
    icon: '/assets/figma/luma-icon.svg',
    mono: true,
    hosts: ['lu.ma', 'luma.com'],
  },
  calendly: {
    label: 'Calendly',
    icon: '/assets/figma/calendly-icon.svg',
    mono: false,
    hosts: ['calendly.com'],
  },
  goldcast: {
    label: 'Goldcast',
    icon: '/assets/figma/goldcast-icon.svg',
    mono: false,
    hosts: ['goldcast.io'],
  },
};

const GENERIC_PLATFORM: PlatformDefinition = {
  label: 'Video',
  icon: null,
  mono: false,
  hosts: [],
};

function getPlatformDefinition(platform: VideoPlatform): PlatformDefinition {
  return platform === 'generic'
    ? GENERIC_PLATFORM
    : PLATFORM_DEFINITIONS[platform];
}

/**
 * Detect video conferencing platform from meeting link URL.
 * Returns null if the input is not a valid URL.
 */
export function getVideoPlatform(meetLink?: string): VideoPlatform | null {
  if (!meetLink || !isValidUrl(meetLink)) {
    return null;
  }

  // Match against the parsed hostname, not the whole URL string: a substring
  // scan would label `zoom.us.evil.example` as Zoom. Exact-or-subdomain matching
  // still accepts regional hosts like `us02web.zoom.us`.
  const hostname = getMeetingLinkHost(meetLink)?.toLowerCase();
  if (!hostname) {
    return null;
  }

  for (const [platform, definition] of Object.entries(PLATFORM_DEFINITIONS)) {
    if (
      definition.hosts.some(
        host => hostname === host || hostname.endsWith(`.${host}`)
      )
    ) {
      return platform as VideoPlatform;
    }
  }

  // A conferencing tool we ship no mark for — the ladder resolves its icon.
  return 'generic';
}

/** Platform icon paths */
export const platformIcons: Record<VideoPlatform, string | null> =
  Object.freeze(
    Object.fromEntries([
      ...Object.entries(PLATFORM_DEFINITIONS).map(([key, d]) => [key, d.icon]),
      ['generic', null],
    ])
  ) as Record<VideoPlatform, string | null>;

/** Platform display names for accessibility */
export const platformNames: Record<VideoPlatform, string> = Object.freeze(
  Object.fromEntries([
    ...Object.entries(PLATFORM_DEFINITIONS).map(([key, d]) => [key, d.label]),
    ['generic', GENERIC_PLATFORM.label],
  ])
) as Record<VideoPlatform, string>;

/** Favicon size to request. 64 is the smallest that stays crisp at 2x. */
const FAVICON_SIZE = 64;

/**
 * Milliseconds to wait for an externally-hosted icon image to load before
 * advancing the icon ladder. Applies to both external rungs — the provider's
 * own icon and the favicon (google.com/s2/favicons). Neither host is under our
 * control, and either could stall the row indefinitely because onError only
 * fires on a response failure, not a connection hang.
 */
export const EXTERNAL_ICON_LOAD_TIMEOUT_MS = 4_000;

function getMeetingLinkHost(meetLink: string): string | null {
  try {
    return new URL(meetLink).hostname || null;
  } catch {
    return null;
  }
}

export interface MeetingIconSource {
  readonly src: string;
  /** Tint to `currentColor` — monochrome marks would vanish in dark mode. */
  readonly tint: boolean;
  /**
   * Render on a neutral chip. Third-party art is of unknown contrast (Cal.com's
   * favicon is a near-black tile that disappears on a dark row), and unlike our
   * own marks we cannot recolour it.
   */
  readonly chip: boolean;
  /**
   * When set, the icon renderer must advance the ladder if the image has not
   * fired onLoad or onError within this many milliseconds. Set on the external
   * rungs — the provider icon and the favicon — since either can hang; bundled
   * marks load instantly from the asset bundle and need no timeout.
   */
  readonly timeoutMs?: number;
}

/**
 * Ordered icon candidates for a meeting, best first. The caller renders the
 * first one that loads and steps down on error; an empty result means fall back
 * to a generic video glyph.
 *
 * The order is fidelity-first:
 *  1. Our own brand mark — no third-party request, works offline, and we control
 *     the dark-mode treatment.
 *  2. The provider's own icon, when the calendar handed us one.
 *  3. A favicon resolved from the join URL's host. Lower fidelity, but the only
 *     route for providers with no calendar add-on and no bundled mark — which
 *     production says is a real population (Tapestry, Zuddl, Sequel, and a long
 *     tail of one-org tools).
 */
export function getMeetingIconSources(
  meetLink?: string,
  providerIconUrl?: string
): MeetingIconSource[] {
  const platform = getVideoPlatform(meetLink);
  if (!platform) {
    return [];
  }

  const sources: MeetingIconSource[] = [];

  const definition = getPlatformDefinition(platform);
  if (definition.icon) {
    sources.push({ src: definition.icon, tint: definition.mono, chip: false });
  }
  if (providerIconUrl) {
    if (isValidUrl(providerIconUrl)) {
      // Provider-hosted art on an external host we don't control — guard it with
      // the same load timeout as the favicon rung so a hung request can't stall
      // the row (onError only fires on a response failure, not a connection hang).
      sources.push({
        src: providerIconUrl,
        tint: false,
        chip: true,
        timeoutMs: EXTERNAL_ICON_LOAD_TIMEOUT_MS,
      });
    } else {
      console.warn(
        '[meetingPlatforms] rejected non-http(s) provider icon URL',
        providerIconUrl
      );
    }
  }

  const host = meetLink ? getMeetingLinkHost(meetLink) : null;
  if (host) {
    // Safe by construction: hardcoded https scheme/host; `domain` param is
    // encodeURIComponent'd from a hostname already validated by getVideoPlatform's
    // isValidUrl check above.
    sources.push({
      src: `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=${FAVICON_SIZE}`,
      tint: false,
      chip: true,
      timeoutMs: EXTERNAL_ICON_LOAD_TIMEOUT_MS,
    });
  }

  return sources;
}

/**
 * Accessible label for the meeting's conferencing platform. Prefers the name
 * the provider gave us, since it is more specific than anything we can infer
 * from a hostname ("Webex" vs. the generic "Video").
 */
export function getMeetingPlatformLabel(
  meetLink?: string,
  providerName?: string
): string {
  if (providerName) {
    return providerName;
  }
  const platform = getVideoPlatform(meetLink);
  return platform ? platformNames[platform] : platformNames.generic;
}
